<template>
  <transition name="fade">
    <div v-if="show"
      class="fixed bottom-6 left-6 z-50 bg-[#10006b] text-white p-6 rounded-lg shadow-lg w-[90vw] max-w-md md:w-[90vw] md:max-w-md">
      <div class="flex justify-between items-start">
        <div class="pr-6">
          <h2 class="text-lg font-bold mb-2 md:text-lg">{{ cookieTexts.title }}</h2>
          <p class="text-sm leading-relaxed md:text-sm">
            {{ cookieTexts.descriptionBefore }}
            <a href="/privacy-policy" class="text-orange-400 underline hover:text-orange-300">{{ cookieTexts.privacyPolicy }}</a>{{ cookieTexts.descriptionAfter }}
          </p>
        </div>
        <button @click="close"
          style="position: absolute; top: 16px; right: 16px; background: none; border: none; padding: 0; cursor: pointer;"
          aria-label="Close">
          <img src="../assets/close.png" alt="Close" style="width: 14px; height: 14px; display: block" />
        </button>
      </div>

      <div class="mt-4 flex flex-col gap-2 md:flex-row md:gap-4 justify-start">
        <button class="bg-orange-400 hover:bg-orange-500 text-black font-bold px-4 py-2 rounded text-sm md:text-base" @click="accept">
          {{ cookieTexts.accept }}
        </button>
        <button class="bg-orange-400 hover:bg-orange-500 text-black font-bold px-4 py-2 rounded text-sm md:text-base" @click="decline">
          {{ cookieTexts.decline }}
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { useI18n } from 'vue-i18n';
import { getAnonymousId } from "../utils/anonymous";
import { API_BASE_URL } from "../config/config";
import { AuthService } from "../services/AuthService";
import { pushToDataLayer } from '../utils/gtm';

const { locale, messages } = useI18n();

// 获取Cookie同意文本
const cookieTexts = computed(() => {
  return messages.value[locale.value]?.cookieConsent || {
    title: 'Cookie',
    descriptionBefore: 'This website sets cookies on your computer to improve our site. To learn more about what data we collect and your privacy options, see our ',
    privacyPolicy: 'privacy policy',
    descriptionAfter: '.',
    accept: 'I accept',
    decline: 'No'
  };
});

const CONSENT_API_URL = `${API_BASE_URL}/api/cookie-consent-logs`;

const getIpAddress = async (): Promise<string> => {
  // 尝试多个 IP 检测服务
  const ipServices = [
    { url: 'https://api.ipify.org?format=json', field: 'ip' },
    { url: 'https://api64.ipify.org?format=json', field: 'ip' },
    { url: 'https://ipapi.co/json/', field: 'ip' },
  ];

  for (const service of ipServices) {
    try {
      console.log(`🌍 Trying IP service: ${service.url}`);
      const res = await fetch(service.url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (!res.ok) {
        console.warn(`⚠️ IP service returned status ${res.status}`);
        continue;
      }

      const data = await res.json();
      const ip = data[service.field];

      console.log(`✅ IP detected: ${ip}`);

      if (ip && ip !== '127.0.0.1' && ip !== 'unknown') {
        return ip;
      }
    } catch (err) {
      console.warn(`⚠️ IP service failed:`, service.url, err);
    }
  }

  console.error("❌ All IP detection services failed");
  return "unknown";
};

const getUserAgent = () => navigator.userAgent;

const show = ref(false);

// 声明 gtag 函数类型
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

// 更新 GTM Consent Mode 状态
const updateGTMConsent = (granted: boolean) => {
  const consentState = granted ? 'granted' : 'denied';

  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      'analytics_storage': consentState,
      'ad_storage': consentState,
      'ad_user_data': consentState,
      'ad_personalization': consentState,
      'personalization_storage': consentState,
    });
    console.log('[Consent] Updated to:', consentState);
  }
};

onMounted(() => {
  const consent = localStorage.getItem("cookie_consent");
  if (!consent) {
    show.value = true;
  }
});

const accept = async () => {
  localStorage.setItem("cookie_consent", "accepted");
  show.value = false;

  // 更新 GTM Consent Mode
  updateGTMConsent(true);

  await submitConsentLog("accept");
  pushToDataLayer('cookie_consent_accepted', {});
  pushToDataLayer('click', {
    category: 'CookieConsent',
    label: 'accept',
  });
};

const decline = async () => {
  localStorage.setItem("cookie_consent", "declined");
  show.value = false;

  // 更新 GTM Consent Mode（拒绝）
  updateGTMConsent(false);

  await submitConsentLog("decline");
  pushToDataLayer('cookie_consent_declined', {});
  pushToDataLayer('click', {
    category: 'CookieConsent',
    label: 'decline',
  });
};

const close = async () => {
  show.value = false;
  await submitConsentLog("close");
  pushToDataLayer('click', {
    category: 'CookieConsent',
    label: 'close',
  });
};

const submitConsentLog = async (consentType: "accept" | "decline" | "close") => {
  const anonymousId = getAnonymousId();
  const ipAddress = await getIpAddress();
  const userAgent = getUserAgent();
  const timestamp = new Date().toISOString();
  const payload = {
    data: {
      anonymous_id: anonymousId,
      ip_address: ipAddress,
      user_agent: userAgent,
      consent: consentType,
      timestamp: timestamp,
    },
  };

  try {
    const response = await fetch(CONSENT_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const result = await response.json();
      console.error("Consent log submission failed:", result);
    } else {
      console.log("Consent log submitted:", consentType);
    }
  } catch (error) {
    console.error("Error submitting consent log:", error);
  }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
